/**
 * Phase 7 fleet-monitoring heartbeat (docs/android/PHASE-7-ARCHITECTURE.md
 * SS5). Reports this kiosk's own telemetry periodically so the admin Device
 * Management screen shows real status/health instead of the permanent
 * UNREGISTERED/OFFLINE every device sits at until it has both paired
 * (DevicePairingModal.tsx, reachable from the account menu) AND sent at
 * least one heartbeat - see that modal's own file doc for why those are two
 * separate steps.
 *
 * A no-op wherever this device has not paired yet - `sendDeviceHeartbeat()`
 * throws `NOT_PAIRED` without a network call in that case (deviceApi.ts) -
 * so this is safe to start unconditionally from posData/index.ts's kiosk
 * boot path rather than needing its own isAndroidKiosk()-equivalent gate.
 */
import { LATEST_SCHEMA_VERSION, recordServerContact } from '@kortex/pos-local';
import { getAndroidAppVersion, getDeviceInfo, getNetworkStatus } from './androidBridge';
import { DeviceApiError, sendDeviceHeartbeat } from './deviceApi';
import { clearDeviceCredential } from './deviceCredential';
import { handleRefusal, runPendingDeviceCommands } from './deviceCommands';
import { persistedDeviceId } from './deviceId';
import { getActiveLocalDatabase } from './posData';
import type { PosDataSource } from './posData/types';

/**
 * No admin-configurable knob for this exists (deviceConfigPayloadSchema's
 * syncIntervalSeconds is a different, sync-specific setting) - five minutes
 * is a plain, unconfigurable default: frequent enough that "OFFLINE" on the
 * fleet screen means something happened in the last few minutes, not idle
 * chatter on every device on every poll.
 */
const HEARTBEAT_INTERVAL_MS = 5 * 60_000;

let intervalId: number | null = null;

/**
 * One heartbeat, right now. Exported (not only used internally by the
 * interval below) so DevicePairingModal.tsx can send the first one the
 * instant pairing succeeds, rather than making whoever is watching Device
 * Management wait up to HEARTBEAT_INTERVAL_MS to see anything change.
 */
export async function sendOneHeartbeat(source: PosDataSource): Promise<void> {
  const info = getDeviceInfo();
  const network = getNetworkStatus();
  const diagnostics = await source.getSyncDiagnostics().catch(() => null);
  try {
    await sendDeviceHeartbeat(persistedDeviceId(), {
      appVersion: getAndroidAppVersion() ?? undefined,
      androidVersion: info?.androidVersion,
      // The schema version this build's migrations bring a database to -
      // packages/pos-local applies MIGRATIONS to completion before any
      // LocalDatabase is usable at all, so this always matches what is
      // actually running by the time a source exists to call this with.
      dbSchemaVersion: LATEST_SCHEMA_VERSION,
      kioskActive: info?.kioskActive,
      networkOnline: network?.online ?? navigator.onLine,
      pendingSyncCount: diagnostics?.pending ?? undefined,
      failedSyncCount: diagnostics?.failed ?? undefined,
      // Distinct from the others above: the server treats `undefined` as
      // "say nothing" but an explicit `null` as "clear it" (service.ts's
      // recordHeartbeat() checks `=== undefined`, not `??`) - so a device
      // with diagnostics but no current error must send null, not have it
      // collapse to undefined the way `?? undefined` would.
      lastSyncError: diagnostics ? diagnostics.lastError : undefined,
    });
  } catch (error) {
    if (await handleRefusal(error)) return;
    if (error instanceof DeviceApiError && error.status === 401) {
      // The server no longer recognises this secret - re-paired elsewhere,
      // or a fresh pairing issued after decommissioning. Stop sending a
      // secret it will only ever reject; "Pair this device" in the account
      // menu is how it gets a new one.
      clearDeviceCredential();
    } else if (!(error instanceof DeviceApiError && error.code === 'NOT_PAIRED')) {
      // NOT_PAIRED is the expected, silent steady state before this device
      // has ever been paired - anything else (network blip, 403 disabled/
      // decommissioned, 5xx) is worth a log line, but never worth
      // surfacing to whoever is standing at the till.
      console.warn('[device] heartbeat failed', error);
    }
    return;
  }
  // The server answered: the till lock's clock starts again.
  const db = getActiveLocalDatabase();
  if (db) await recordServerContact(db.config).catch(() => undefined);
  // Commands ride on the heartbeat: no second timer, and nothing polled on an unpaired device.
  await runPendingDeviceCommands();
}

/**
 * Started once per kiosk boot (posData/index.ts's createSource()); stopped
 * by resetPosDataSource() on sign-out so a later session's boot never
 * stacks a second interval on top of this one.
 */
export function startDeviceHeartbeat(source: PosDataSource): void {
  if (intervalId !== null) return;
  void sendOneHeartbeat(source);
  intervalId = window.setInterval(() => void sendOneHeartbeat(source), HEARTBEAT_INTERVAL_MS);
}

export function stopDeviceHeartbeat(): void {
  if (intervalId === null) return;
  window.clearInterval(intervalId);
  intervalId = null;
}
